import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { formatApiError } from "../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_opport-biz-ma/artifacts/oknd9jsa_Logo%20Club%20Business%20Maroc%20Annuaire.png";

export default function RegisterPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [form, setForm] = useState({ nom_prenom: "", password: "", confirm: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/auth/invite/${token}`)
      .then(({ data }) => { setInvite(data); setForm(f => ({ ...f, nom_prenom: data.name || "" })); })
      .catch(() => setInvalid(true));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) { setError("Les mots de passe ne correspondent pas"); return; }
    if (form.password.length < 8) { setError("Mot de passe trop court (min. 8 caractères)"); return; }
    if (!legalAccepted) { setError("Vous devez accepter les Conditions d'utilisation et la Politique de confidentialité pour créer votre compte."); return; }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/api/auth/register`, { token, password: form.password, nom_prenom: form.nom_prenom });
      setSuccess(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(135deg, #081026 0%, #0D1B3E 50%, #081530 100%)" }}>
      <div className="absolute inset-0 pointer-events-none opacity-5" style={{ backgroundImage: "radial-gradient(circle, #C9A227 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
      <div className="relative z-10 w-full max-w-sm mx-4 animate-fade-in-up">
        <div className="flex flex-col items-center mb-8">
          <img src={LOGO_URL} alt="Club Business Maroc" className="w-24 h-24 object-contain rounded-xl mb-2" />
        </div>
        <div className="cbm-glass rounded-2xl p-8 shadow-card">
          {invalid ? (
            <div className="text-center">
              <p className="text-red-400 font-outfit text-lg mb-2">Invitation invalide</p>
              <p className="text-cbm-muted text-sm mb-4">Ce lien est expiré ou a déjà été utilisé.</p>
              <Link to="/login" className="cbm-btn-ghost text-sm border border-white/10">Retour à la connexion</Link>
            </div>
          ) : success ? (
            <div className="text-center">
              <CheckCircle2 size={40} className="text-green-400 mx-auto mb-3" />
              <h2 className="font-outfit font-semibold text-white text-xl mb-2">Compte créé !</h2>
              <p className="text-cbm-secondary text-sm mb-4">Votre compte est en attente d'approbation par l'administrateur. Vous recevrez un email dès que votre accès est activé.</p>
              <Link to="/login" className="cbm-btn-primary text-sm">Retour à la connexion</Link>
            </div>
          ) : invite ? (
            <>
              <h1 className="font-outfit text-2xl font-semibold text-white mb-1 text-center">Créer mon compte</h1>
              <p className="text-cbm-muted text-sm text-center mb-6">Invitation pour <span className="text-gold">{invite.email}</span></p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="cbm-label block mb-1.5">Nom & Prénom *</label>
                  <input data-testid="register-name" className="cbm-input" placeholder="Votre nom complet" value={form.nom_prenom} onChange={e => setForm(f => ({ ...f, nom_prenom: e.target.value }))} required />
                </div>
                <div>
                  <label className="cbm-label block mb-1.5">Email</label>
                  <input className="cbm-input opacity-60 cursor-not-allowed" value={invite.email} readOnly />
                </div>
                <div>
                  <label className="cbm-label block mb-1.5">Mot de passe *</label>
                  <div className="relative">
                    <input data-testid="register-password" type={showPwd ? "text" : "password"} className="cbm-input pr-10" placeholder="Min. 8 caractères" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required />
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-cbm-muted hover:text-cbm-secondary" onClick={() => setShowPwd(!showPwd)}>
                      {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="cbm-label block mb-1.5">Confirmer le mot de passe *</label>
                  <input data-testid="register-confirm" type="password" className="cbm-input" placeholder="Répétez votre mot de passe" value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} required />
                </div>
                
                {/* Checkbox acceptation conditions légales */}
                <div className="flex items-start gap-2.5 pt-2">
                  <input
                    data-testid="register-legal-checkbox"
                    type="checkbox"
                    id="legal-accept"
                    checked={legalAccepted}
                    onChange={(e) => setLegalAccepted(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-cbm-muted/30 bg-navy/50 text-gold focus:ring-gold focus:ring-offset-0 cursor-pointer"
                  />
                  <label htmlFor="legal-accept" className="text-xs text-cbm-secondary leading-relaxed cursor-pointer">
                    J'accepte les{" "}
                    <a
                      href="/conditions"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gold hover:underline"
                      data-testid="register-legal-link"
                    >
                      Conditions d'utilisation et la Politique de confidentialité
                    </a>{" "}
                    du Club Business Maroc.
                  </label>
                </div>

                {error && <div className="bg-red-900/20 border border-red-800/30 text-red-400 rounded-lg px-4 py-2.5 text-sm" data-testid="register-error">{error}</div>}
                <button data-testid="register-submit" type="submit" disabled={submitting} className="cbm-btn-primary w-full justify-center text-sm py-2.5">
                  {submitting ? <><span className="w-4 h-4 border-2 border-navy border-t-transparent rounded-full animate-spin" />Création...</> : "Créer mon compte"}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
